import { ReactElement, useEffect, useRef, useState } from 'react';
import { AnimatePresence, m } from 'framer-motion';
import { driveState, useDriveStore } from '../drive-store';
import { IconButton } from '@ui/buttons/icon-button';
import { Button } from '@ui/buttons/button';
import { CloseIcon } from '@ui/icons/material/Close';
import { RefreshIcon } from '@ui/icons/material/Refresh';
import { useFileUploadStore } from '@common/uploads/uploader/file-upload-provider';
import { Trans } from '@ui/i18n/trans';
import { UploadQueueItem } from './upload-queue-item';
import { useVirtualizer } from '@tanstack/react-virtual';
// import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { ChevronDownIcon } from '@ui/icons/material/chevron-down-icon';
import { ChevronUpIcon } from '@ui/icons/material/chevron-up-icon';

export function UploadQueue() {
  const isOpen = useDriveStore(s => s.uploadQueueIsOpen);
  const setOpen = driveState().setUploadQueueIsOpen;

  const fileUploads = useFileUploadStore(s => s.fileUploads);
  const uploadsCount = useFileUploadStore(s => s.fileUploads.size);
  const [isMinimized, setIsMinimized] = useState(false);

  const prevIds = useRef<Set<string>>(new Set());
  const queueHeight = Math.min(uploadsCount * 60, 320);

  useEffect(() => {
    const uploads = [...fileUploads.values()];
    const currentIds = new Set(uploads.map(u => u.file.id));

    const hasNewUpload = uploads.some(
      u => !prevIds.current.has(u.file.id) && u.status === 'inProgress',
    );

    if (hasNewUpload) {
      setOpen(true);
      setIsMinimized(false);
    }

    prevIds.current = currentIds;
  }, [fileUploads]);

  return (
    <AnimatePresence>
      {isOpen && (
        <m.div
          key="upload-queue"
          className="fixed bottom-4 right-4 z-[9999] w-[350px] rounded border bg text-sm shadow-xl"
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
        >
          <div className="flex flex-col">
            <Header
              isMinimized={isMinimized}
              onToggleMinimize={() => setIsMinimized(prev => !prev)}
            />

            <AnimatePresence initial={false}>
              {!isMinimized && (
                <m.div
                  key="content"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: queueHeight, opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <UploadList />
                </m.div>
              )}
            </AnimatePresence>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
}

export function Header({
  onToggleMinimize,
  isMinimized,
}: {
  onToggleMinimize: () => void;
  isMinimized: boolean;
}) {
  const inProgressUploadsCount = useFileUploadStore(s => s.activeUploadsCount);
  const completedUploadsCount = useFileUploadStore(
    s => s.completedUploadsCount,
  );
  const clearInactive = useFileUploadStore(s => s.clearInactive);
  const fileUploads = useFileUploadStore(s => s.fileUploads);
  const cancelUploads = useFileUploadStore(s => s.cancelUploads);
  const retryAllFailed = useFileUploadStore(s => s.retryAllFailed);

  // Count failed and aborted uploads
  const failedUploadsCount = [...fileUploads.values()].filter(
    u => u.status === 'failed' || u.status === 'aborted'
  ).length;

  let message: ReactElement;
  if (inProgressUploadsCount) {
    message = (
      <Trans
        message="Uploading :count files"
        values={{ count: inProgressUploadsCount }}
      />
    );
  } else if (completedUploadsCount) {
    message = (
      <Trans
        message="Uploaded :count files"
        values={{ count: completedUploadsCount }}
      />
    );
  } else {
    message = <Trans message="No active uploads" />;
  }

  // only allow closing upload queue if there are no active uploads
  return (
    <div className="flex min-h-[45px] items-center justify-between gap-10 border-b bg-alt px-10 py-4">
      {message}
      <div className="flex items-center gap-8">
        {failedUploadsCount > 0 && (
          <Button
            size="xs"
            variant="outline"
            className="border-danger text-danger"
            startIcon={<RefreshIcon />}
            onClick={() => retryAllFailed()}
          >
            <Trans
              message="Retry all (:count)"
              values={{ count: failedUploadsCount }}
            />
          </Button>
        )}
        <IconButton size="md" onClick={onToggleMinimize}>
          {isMinimized ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </IconButton>
        <IconButton
          size="sm"
          onClick={() => {
            const hasOngoing = [...fileUploads.values()].some(
              upload => upload.status === 'inProgress' || upload.status === 'pending',
            );
            if (hasOngoing) {
              const shouldStopUploading = window.confirm(
                'Are you sure you want to stop uploading?\n\nYour ongoing uploads will be canceled and may need to be restarted.',
              );

              if (!shouldStopUploading) {
                return;
              }
            }

            cancelUploads();
            driveState().setUploadQueueIsOpen(false);

            // wait for upload queue panel animation to complete, then clear inactive uploads
            setTimeout(() => {
              clearInactive();
            }, 200);
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>
    </div>
  );
}

function UploadList() {
  const uploads = useFileUploadStore(s => s.fileUploads);
  const uploadsArray = [...uploads.values()];
  const ref = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: uploads.size,
    getScrollElement: () => ref.current,
    estimateSize: () => 60,
    overscan: 4,
  });

  return (
    <div className="max-h-320 overflow-y-auto" ref={ref}>
      <div
        className="relative w-full"
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualizer.getVirtualItems().map(virtualItem => {
          const upload = uploadsArray[virtualItem.index];
          return (
            <UploadQueueItem
              style={{
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
              key={upload.file.id}
              file={upload.file}
            />
          );
        })}
      </div>
    </div>
  );
}
